import { useMutation } from "@pinia/colada";
import { DynamicImage, type DynamicImageUpdate, useApi } from "@soft-db/monitoring-api-client";
import {
	composeCacheEffects,
	useInvalidator,
	useQueryEnabled,
	useScopeDataUpdater,
	useScopedQuery,
} from "./query";

export const dynamicImagesKeys = {
	root: ["dynamic-images"] as const,

	bySite: (site: string) => [...dynamicImagesKeys.root, "site", site] as const,

	byId: (id: string) => [...dynamicImagesKeys.root, "id", id] as const,
};

// --- Cache Effects ---

function useInvalidateSiteDynamicImages() {
	const invalidate = useInvalidator();

	return (dyn: DynamicImage) => {
		invalidate({
			keys: [dynamicImagesKeys.bySite(dyn.site)],
		});
	};
}

function useUpdateCacheById() {
	const update = useScopeDataUpdater();

	return (dyn: DynamicImage) => {
		update(dynamicImagesKeys.byId(dyn.id), dyn);
	};
}

function useDynamicImageCacheEffects() {
	return composeCacheEffects(useInvalidateSiteDynamicImages(), useUpdateCacheById());
}

// --- Queries ---

export function useSiteDynamicImages(site: MaybeRefOrGetter<string>) {
	const api = useApi().dynamicImage();

	const { data: dynamicImages, ...rest } = useScopedQuery({
		key: dynamicImagesKeys.bySite(toValue(site)),
		query: () => api.fetchDynamicImages(toValue(site)),
	});

	return {
		dynamicImages,
		data: dynamicImages,
		...rest,
	};
}

export function useDynamicImage(id: MaybeRefOrGetter<string>) {
	const api = useApi().dynamicImage();

	const { data: dynamicImage, ...rest } = useScopedQuery({
		key: () => dynamicImagesKeys.byId(toValue(id)),
		query: () => api.fetch(toValue(id)),
		enabled: useQueryEnabled(id),
	});

	return {
		dynamicImage,
		data: dynamicImage,
		...rest,
	};
}

// --- Mutations ---

export function useCreateDynamicImage() {
	const api = useApi().dynamicImage();

	return useMutation({
		mutation: ({ site, data }: { site: string; data: DynamicImageUpdate }) =>
			api.create(site, data),
		onSuccess: useDynamicImageCacheEffects(),
	});
}

export function useUpdateDynamicImage() {
	const api = useApi().dynamicImage();

	return useMutation({
		mutation: ({ id, data }: { id: string; data: DynamicImageUpdate }) => api.update(id, data),
		onSuccess: useDynamicImageCacheEffects(),
	});
}

export function useDeleteDynamicImage() {
	const api = useApi().dynamicImage();

	return useMutation({
		mutation: async (id: string) => {
			const dyn = await api.fetch(id);
			await api.delete(id);
			return dyn;
		},
		onSuccess: useInvalidateSiteDynamicImages(),
	});
}
